import { useState, useEffect, useCallback, useRef } from "react";
import inventoryApi from './inventoryApi';

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
function timeAgo(dateString) {
  if (!dateString) return "—";
  const now = new Date();
  const then = new Date(dateString);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 30) return "just now";
  if (diff < 90) return "a minute ago";
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 7200) return "an hour ago";
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 172800) return "yesterday";
  if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
  if (diff < 1209600) return "a week ago";
  if (diff < 2592000) return `${Math.floor(diff / 604800)} weeks ago`;
  return `${Math.floor(diff / 2592000)} months ago`;
}

function formatExact(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getSeverity(remaining, threshold) {
  if (remaining === 0) return "CRITICAL";
  if (threshold > 0 && remaining < threshold / 2) return "HIGH";
  return "LOW";
}

function normalizeList(data) {
  if (Array.isArray(data)) return { results: data, count: data.length, next: null, previous: null };
  if (data && Array.isArray(data.results)) return data;
  return { results: [], count: 0, next: null, previous: null };
}

/* ─────────────────────────────────────────────
   SEVERITY BADGE
───────────────────────────────────────────── */
function SeverityBadge({ remaining, threshold }) {
  const level = getSeverity(remaining, threshold);
  const cfg = {
    CRITICAL: { label: "Critical", cls: "bg-rose-600 text-white border-rose-600" },
    HIGH:     { label: "High",     cls: "bg-orange-500 text-white border-orange-500" },
    LOW:      { label: "Low",      cls: "bg-amber-400 text-amber-900 border-amber-400" },
  }[level];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold border ${cfg.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
      {cfg.label}
    </span>
  );
}

/* ─────────────────────────────────────────────
   CATEGORY BADGE
───────────────────────────────────────────── */
function CategoryBadge({ category }) {
  if (category === "CONSUMABLE")
    return <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-100 text-violet-700 border border-violet-200 uppercase tracking-wide">Cons.</span>;
  if (category === "CAPITAL")
    return <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-wide">Cap.</span>;
  return null;
}

/* ─────────────────────────────────────────────
   TOOLTIP WRAPPER
───────────────────────────────────────────── */
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 whitespace-nowrap bg-slate-800 text-white text-xs px-2.5 py-1.5 rounded-lg shadow-xl pointer-events-none">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
        </span>
      )}
    </span>
  );
}

/* ─────────────────────────────────────────────
   STAT CHIP
───────────────────────────────────────────── */
function StatChip({ count, label, variant }) {
  const styles = {
    red:  "bg-rose-50 border-rose-200 text-rose-700",
    slate:"bg-slate-50 border-slate-200 text-slate-700",
  };
  const dotStyles = {
    red:  "bg-rose-500",
    slate:"bg-slate-400",
  };
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm font-semibold ${styles[variant]}`}>
      <span className={`w-2 h-2 rounded-full ${dotStyles[variant]} ${variant === "red" ? "animate-pulse" : ""}`} />
      <span className="font-bold">{count ?? "—"}</span>
      <span className="font-medium opacity-80">{label}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ALERT ROW
───────────────────────────────────────────── */
function AlertRow({ alert, onResolve }) {
  const [confirmMode, setConfirmMode] = useState(false);
  const [resolving, setResolving] = useState(false);

  const item   = alert.inventory_item_details ?? {};
  const tech   = alert.technician_details ?? {};
  const isResolved = alert.resolved;

  const handleConfirm = async () => {
    setResolving(true);
    await onResolve(alert.id, () => setResolving(false));
  };

  return (
    <tr className={`border-b border-slate-100 hover:bg-slate-50/60 transition-colors group ${isResolved ? "opacity-60" : ""}`}>
      {/* Item */}
      <td className="px-4 py-3.5">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm">{item.item_name ?? "—"}</span>
            <CategoryBadge category={item.category} />
          </div>
          {item.item_code && (
            <span className="text-[11px] text-slate-400 font-mono">{item.item_code}</span>
          )}
        </div>
      </td>

      {/* Technician */}
      <td className="px-4 py-3.5 text-sm text-slate-700 font-medium">
        {tech.name ?? "—"}
      </td>

      {/* Remaining */}
      <td className="px-4 py-3.5">
        <span className="text-rose-600 font-bold text-base tabular-nums">
          {alert.quantity_remaining ?? 0}
        </span>
      </td>

      {/* Threshold */}
      <td className="px-4 py-3.5 text-slate-400 font-medium tabular-nums text-sm">
        {alert.low_stock_threshold ?? 0}
      </td>

      {/* Severity */}
      <td className="px-4 py-3.5">
        <SeverityBadge
          remaining={alert.quantity_remaining ?? 0}
          threshold={alert.low_stock_threshold ?? 0}
        />
      </td>

      {/* Triggered */}
      <td className="px-4 py-3.5">
        <Tooltip text={formatExact(alert.triggered_at)}>
          <span className="text-sm text-slate-500 cursor-default border-b border-dashed border-slate-300">
            {timeAgo(alert.triggered_at)}
          </span>
        </Tooltip>
      </td>

      {/* Status */}
      <td className="px-4 py-3.5">
        {isResolved ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Resolved
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            Unresolved
          </span>
        )}
      </td>

      {/* Action */}
      <td className="px-4 py-3.5 text-right">
        {!isResolved && (
          <>
            {!confirmMode ? (
              <button
                onClick={() => setConfirmMode(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold transition-colors shadow-sm"
              >
                Resolve
              </button>
            ) : (
              <div className="flex items-center gap-1.5 justify-end">
                <button
                  onClick={handleConfirm}
                  disabled={resolving}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold transition-colors disabled:opacity-70 min-w-[76px] justify-center"
                >
                  {resolving ? (
                    <>
                      <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Wait…
                    </>
                  ) : (
                    "Confirm?"
                  )}
                </button>
                {!resolving && (
                  <button
                    onClick={() => setConfirmMode(false)}
                    className="w-6 h-6 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 text-sm font-bold transition-colors"
                    title="Cancel"
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

/* ─────────────────────────────────────────────
   EMPTY STATE
───────────────────────────────────────────── */
function EmptyState({ unresolvedOnly }) {
  return (
    <tr>
      <td colSpan={8}>
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          {unresolvedOnly ? (
            <>
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-2xl mb-4">
                ✓
              </div>
              <p className="font-semibold text-emerald-700 text-base">
                No unresolved low stock alerts
              </p>
              <p className="text-sm text-slate-400 mt-1">
                All technician inventory levels are healthy.
              </p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-2xl mb-4">
                📋
              </div>
              <p className="font-semibold text-slate-600 text-base">No alerts found</p>
              <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filters.</p>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ─────────────────────────────────────────────
   PAGINATION
───────────────────────────────────────────── */
function Pagination({ page, hasNext, hasPrev, total, onPrev, onNext }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/60">
      <p className="text-xs text-slate-500">
        {total != null ? (
          <>
            <span className="font-semibold text-slate-700">{total}</span> alert{total !== 1 ? "s" : ""} total
          </>
        ) : null}
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={onPrev}
          disabled={!hasPrev}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          ← Previous
        </button>
        <span className="text-sm font-semibold text-slate-700 px-1">
          Page {page}
        </span>
        <button
          onClick={onNext}
          disabled={!hasNext}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ERROR STATE
───────────────────────────────────────────── */
function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 text-center px-6">
      <div className="w-16 h-16 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center text-2xl">
        ⚠
      </div>
      <div>
        <p className="text-base font-bold text-slate-700">Failed to load alerts</p>
        <p className="text-sm text-slate-500 mt-1 max-w-sm">{message || "An unexpected error occurred."}</p>
      </div>
      <button
        onClick={onRetry}
        className="px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition-colors shadow-sm"
      >
        Retry
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   LOADING SKELETON
───────────────────────────────────────────── */
function SkeletonRows() {
  return Array.from({ length: 5 }).map((_, i) => (
    <tr key={i} className="border-b border-slate-100">
      {Array.from({ length: 8 }).map((_, j) => (
        <td key={j} className="px-4 py-4">
          <div
            className="h-4 bg-slate-200 rounded animate-pulse"
            style={{ width: `${[60, 50, 30, 30, 50, 55, 55, 40][j]}%`, animationDelay: `${i * 60}ms` }}
          />
        </td>
      ))}
    </tr>
  ));
}

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
export default function LowStockAlertsPage() {
  const [alerts, setAlerts]               = useState([]);
  const [totalCount, setTotalCount]       = useState(null);
  const [unresolvedCount, setUnresolvedCount] = useState(null);
  const [hasNext, setHasNext]             = useState(false);
  const [hasPrev, setHasPrev]             = useState(false);
  const [page, setPage]                   = useState(1);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [search, setSearch]               = useState("");
  const searchDebounce                    = useRef(null);

  /* ── fetch alerts ── */
  const fetchAlerts = useCallback(async (opts = {}) => {
    const {
      pg = page,
      onlyUnresolved = unresolvedOnly,
    } = opts;

    setLoading(true);
    setError(null);
    try {
      const params = { page: pg };
      if (onlyUnresolved) params.resolved = false;

      let data;
      if (onlyUnresolved) {
        // Use the dedicated unresolved endpoint for page 1, fallback to filtered list for pagination
        if (pg === 1) {
          data = await inventoryApi.getUnresolvedAlerts();
        } else {
          data = await inventoryApi.getAlerts(params);
        }
      } else {
        data = await inventoryApi.getAlerts(params);
      }

      const norm = normalizeList(data);
      setAlerts(norm.results);
      setTotalCount(norm.count);
      setHasNext(!!norm.next);
      setHasPrev(!!norm.previous);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to load alerts.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [page, unresolvedOnly]);

  /* ── fetch unresolved count for header chip ── */
  const fetchUnresolvedCount = useCallback(async () => {
    try {
      const data = await inventoryApi.getUnresolvedAlerts();
      const norm = normalizeList(data);
      setUnresolvedCount(norm.count);
    } catch {
      // non-critical
    }
  }, []);

  /* ── initial + filter change load ── */
  useEffect(() => {
    setPage(1);
    fetchAlerts({ pg: 1, onlyUnresolved: unresolvedOnly });
    fetchUnresolvedCount();
  }, [unresolvedOnly]);

  /* ── page change ── */
  useEffect(() => {
    fetchAlerts({ pg: page });
  }, [page]);

  /* ── search debounce (client-side filter on loaded results) ── */
  const filteredAlerts = search.trim()
    ? alerts.filter((a) => {
        const q = search.toLowerCase();
        const itemName = a.inventory_item_details?.item_name?.toLowerCase() ?? "";
        const techName = a.technician_details?.name?.toLowerCase() ?? "";
        return itemName.includes(q) || techName.includes(q);
      })
    : alerts;

  /* ── resolve handler ── */
  const handleResolve = useCallback(async (id, onDone) => {
    // Optimistic update
    setAlerts((prev) =>
      prev.map((a) =>
        a.id === id
          ? { ...a, resolved: true, resolved_at: new Date().toISOString() }
          : a
      )
    );
    setUnresolvedCount((n) => (n != null ? Math.max(0, n - 1) : n));

    try {
      await inventoryApi.resolveAlert(id);
    } catch {
      // Revert on error
      setAlerts((prev) =>
        prev.map((a) =>
          a.id === id ? { ...a, resolved: false, resolved_at: null } : a
        )
      );
      setUnresolvedCount((n) => (n != null ? n + 1 : n));
    } finally {
      onDone?.();
    }
  }, []);

  const handleRefresh = () => {
    fetchAlerts({ pg: page });
    fetchUnresolvedCount();
  };

  /* ─────────────────────────────────────────
     RENDER
  ───────────────────────────────────────── */
  return (
    <>
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-in { animation: fade-in 0.25s ease-out forwards; }
      `}</style>

      <div className="min-h-screen bg-slate-50 font-sans fade-in">

        {/* ── PAGE HEADER ── */}
        <div className="bg-white border-b border-slate-100 px-6 py-5">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">

            {/* Left: title + chips */}
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center text-white text-lg shadow">
                  ⚠
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900 leading-tight">
                    Low Stock Alerts
                  </h1>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Monitor and resolve inventory shortages
                  </p>
                </div>
              </div>
            </div>

            {/* Right: chips + refresh */}
            <div className="flex items-center gap-3 flex-wrap">
              <StatChip count={unresolvedCount} label="Unresolved" variant="red" />
              <StatChip count={totalCount}      label="Total Alerts" variant="slate" />
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <svg
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* ── FILTER BAR ── */}
        <div className="bg-white border-b border-slate-100 px-6 py-3">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">

            {/* Toggle */}
            <div className="flex rounded-xl border border-slate-200 overflow-hidden bg-slate-50 p-0.5 shrink-0">
              {[
                { key: true,  label: "Unresolved Only" },
                { key: false, label: "All Alerts" },
              ].map(({ key, label }) => (
                <button
                  key={String(key)}
                  onClick={() => { setUnresolvedOnly(key); setPage(1); }}
                  className={`px-3.5 py-1.5 text-sm font-semibold rounded-lg transition-all ${
                    unresolvedOnly === key
                      ? "bg-white text-slate-800 shadow-sm border border-slate-200"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative flex-1 min-w-0 w-full sm:max-w-xs">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search item or technician…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-1.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition bg-slate-50 placeholder-slate-400"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-lg leading-none"
                >
                  ×
                </button>
              )}
            </div>

            {search && (
              <p className="text-xs text-slate-400 shrink-0">
                {filteredAlerts.length} result{filteredAlerts.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          {error ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <ErrorState message={error} onRetry={handleRefresh} />
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      {[
                        "Item",
                        "Technician",
                        "Remaining",
                        "Threshold",
                        "Severity",
                        "Triggered",
                        "Status",
                        "",
                      ].map((h, i) => (
                        <th
                          key={i}
                          className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider ${i === 7 ? "text-right" : ""}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <SkeletonRows />
                    ) : filteredAlerts.length === 0 ? (
                      <EmptyState unresolvedOnly={unresolvedOnly && !search} />
                    ) : (
                      filteredAlerts.map((alert) => (
                        <AlertRow
                          key={alert.id}
                          alert={alert}
                          onResolve={handleResolve}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination (hidden while searching client-side) */}
              {!loading && !error && !search && (hasNext || hasPrev) && (
                <Pagination
                  page={page}
                  hasNext={hasNext}
                  hasPrev={hasPrev}
                  total={totalCount}
                  onPrev={() => setPage((p) => p - 1)}
                  onNext={() => setPage((p) => p + 1)}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}